import { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumbComp from "../../components/breadCrumb";
import HighchartsComponent from "../../components/highchartsComponent";
import SimpleTable from "../../components/tables/simpleTable";
import LaddaButton from "../../components/buttons/laddaButton";
import { useNavigate } from "react-router-dom";
import {
  callApiAsync,
  getInterestedPartiesByProcess,
  getInterestedPartiesGraphData
} from "../../services/apiService";
import { icons } from "../../constants";
import { interested_parties_enter } from "../../constants/enums";
import {
  stakeholderlevel,
  stakeholderRelationselect,
  stakeholdersinfluence,
  stakeholdersRolType
} from "../../constants/selectData";
import { StyledTooltip } from "../../components/toolltipComponent";
import "./styles.css";

const buildGroupedColumnOptions = (categories, internalSeries, externalSeries) => ({
  chart: {
    type: "column"
  },
  title: {
    text: ""
  },
  credits: {
    enabled: false
  },
  xAxis: {
    categories,
    crosshair: true,
    labels: {
      rotation: -32,
      style: { fontSize: "11px" }
    }
  },
  yAxis: {
    min: 0,
    allowDecimals: false,
    title: {
      text: "Cantidad de partes interesadas"
    }
  },
  tooltip: {
    shared: true,
    headerFormat: "<b>{point.key}</b><br/>",
    pointFormat:
      '<span style="color:{series.color}">\u25CF </span>{series.name}: <b>{point.y}</b><br/>'
  },
  plotOptions: {
    column: {
      grouping: true,
      pointPadding: 0.08,
      groupPadding: 0.12,
      borderWidth: 0
    }
  },
  legend: {
    align: "center",
    verticalAlign: "bottom",
    layout: "horizontal"
  },
  series: [
    {
      name: "Internas",
      data: internalSeries,
      color: "#5B7FD1"
    },
    {
      name: "Externas",
      data: externalSeries,
      color: "#E8A838"
    }
  ]
});

const summaryColumns = [
  {
    field: "enter",
    header: "Entrada",
    columnType: "custom-frozen",
    borderRadius: "left",
    render: (rowData) => (
      <p className="table-default-column">
        {(rowData.enter === 1 && interested_parties_enter[1]) ||
          (rowData.enter === 2 && interested_parties_enter[2])}
      </p>
    )
  },
  {
    field: "interested_parties",
    header: "Partes interesadas",
    columnType: "custom-frozen",
    render: (rowData) => (
      <p className="table-default-column">{rowData.interested_parties}</p>
    )
  },
  {
    field: "rol_type",
    header: (
      <StyledTooltip
        title={
          <>
            <p className="tooltip_info_title">Tipo de Rol</p>
            <div className="VContainer">
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Decisor:</span> Tiene
                autoridad para aprobar o rechazar decisiones clave.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Financiador:</span> Aporta
                recursos económicos o materiales.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Ejecutor:</span> Participa
                directamente en la implementación de actividades.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Usuario final:</span> Recibe
                o utiliza el producto/ servicio del proyecto.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Regulador:</span> Supervisa el
                cumplimiento de normas y leyes.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Influenciador:</span> Puede
                moldear opiniones o decisiones sin tener poder formal.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Observador:</span> No interviene
                directamente, pero está atento al desarrollo del proyecto.
              </p>
            </div>
          </>
        }
        placement="top"
      >
        <div className="HContainer pointer">
          <span className="no-wrap mr-10 ">Tipo de rol</span>
          <img
            src={icons.infoExclamationWhite}
            alt="info"
            className="btn-icon-table"
          />
        </div>
      </StyledTooltip>
    ),
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {stakeholdersRolType.find((opt) => opt.value === rowData.rol_type)
          ?.name || "-"}
      </p>
    )
  },
  {
    field: "relation_type",
    header: (
      <StyledTooltip
        title={
          <>
            <p className="tooltip_info_title">Tipo de relación</p>
            <div className="VContainer">
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Directa:</span> El proveedor
                que entrega materiales esenciales para la ejecución.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Indirecta:</span> La comunidad
                que se beneficia del impacto ambiental positivo del proyecto.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Formal:</span> Contratos,
                convenios, acuerdos legales.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Informal:</span> Influencia
                por reputación, redes sociales o vínculos personales.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Permanente:</span> Empleados de
                planta o socios estratégicos.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Temporal:</span> Consultores,
                contratistas o voluntarios por proyecto.
              </p>
            </div>
          </>
        }
        placement="top"
      >
        <div className="HContainer pointer">
          <span className="no-wrap mr-10 ">Tipo de relacion</span>
          <img
            src={icons.infoExclamationWhite}
            alt="info"
            className="btn-icon-table"
          />
        </div>
      </StyledTooltip>
    ),
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {stakeholderRelationselect.find(
          (opt) => opt.value === rowData.relation_type
        )?.name || "-"}
      </p>
    )
  },
  {
    field: "influence_type",
    header: (
      <StyledTooltip
        title={
          <>
            <p className="tooltip_info_title">Tipo de influencia</p>
            <div className="VContainer">
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Esenciales:</span> El
                proveedor que entrega materiales esenciales para la ejecución.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Importantes:</span> La
                comunidad que se beneficia del impacto ambiental positivo del
                proyecto.
              </p>
              <p className="tooltip_text">
                <span className="tooltip_subtitle">Cooperadores:</span>{" "}
                Contratos, convenios, acuerdos legales.
              </p>
            </div>
          </>
        }
        placement="top"
      >
        <div className="HContainer pointer">
          <span className="no-wrap mr-10 ">Tipo de influencia</span>
          <img
            src={icons.infoExclamationWhite}
            alt="info"
            className="btn-icon-table"
          />
        </div>
      </StyledTooltip>
    ),
    columnType: "custom",
    render: (rowData) => (
      <p className="table-default-column">
        {stakeholdersinfluence.find(
          (opt) => opt.value === rowData.influence_type
        )?.name || "-"}
      </p>
    )
  },
  {
    field: "influence_level",
    header: "Nivel de influencia",
    columnType: "custom",
    render: (rowData) => (
      <p
        className="table-default-column"
        style={{
          color:
            rowData.influence_level === 1
              ? "#22C55E"
              : rowData.influence_level === 2
              ? "#E5515F"
              : ""
        }}
      >
        {stakeholderlevel.find((opt) => opt.value === rowData.influence_level)
          ?.name || "-"}
      </p>
    )
  }
];

const InterestedPartiesGraph = () => {
  const navigate = useNavigate();
  const [processRows, setProcessRows] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [selectedProcessName, setSelectedProcessName] = useState(null);

  const loadProcessChartData = () => {
    callApiAsync(
      () => getInterestedPartiesGraphData(),
      (data) => {
        const rows = Array.isArray(data) ? data : [];
        setProcessRows(
          rows.map((row) => ({
            process_id: row.process_id,
            name: row.name,
            internal: Number(row.internal) || 0,
            external: Number(row.external) || 0,
            total: Number(row.y) || 0
          }))
        );
      }
    );
  };

  const loadDetailForProcess = useCallback((processId, name) => {
    callApiAsync(
      () => getInterestedPartiesByProcess(processId),
      (data) => {
        setDetailRows(Array.isArray(data) ? data : []);
        setSelectedProcessName(name || null);
      }
    );
  }, []);

  useEffect(() => {
    loadProcessChartData();
  }, []);

  const chartOptions = useMemo(() => {
    if (!processRows.length) {
      return buildGroupedColumnOptions([], [], []);
    }
    const categories = processRows.map((r) => r.name);
    const internalSeries = processRows.map((r) => r.internal);
    const externalSeries = processRows.map((r) => r.external);
    const base = buildGroupedColumnOptions(
      categories,
      internalSeries,
      externalSeries
    );
    return {
      ...base,
      plotOptions: {
        ...base.plotOptions,
        column: {
          ...base.plotOptions.column,
          cursor: "pointer",
          point: {
            events: {
              click: function () {
                const idx = this.index;
                const meta = processRows[idx];
                if (meta?.process_id != null) {
                  loadDetailForProcess(meta.process_id, meta.name);
                }
              }
            }
          }
        }
      }
    };
  }, [processRows, loadDetailForProcess]);

  return (
    <div className="App">
      <BreadCrumbComp />
      <div
        className="searchTitle"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px"
        }}
      >
        <h1 style={{ margin: 0 }}>PILAR DE PARTES INTERESADAS</h1>
        <LaddaButton
          variant="contained"
          margin={0}
          height="40px"
          width="280px"
          fontSize="14px"
          backgroundColor={"#F4D658"}
          onClick={() => {
            navigate("/gestion_HSQ_GCD/interestedParties");
          }}
        >
          <span className="text-black">Ver detalles de partes interesadas</span>
        </LaddaButton>
      </div>

      <div className="table-container">
        <p className="title-bio-details-chard">
          Distribución del total de partes interesadas por proceso (clic en las
          barras para ver el resumen)
        </p>
        <div style={{ minHeight: "420px", marginBottom: "24px" }}>
          <HighchartsComponent options={chartOptions} />
        </div>

        <p className="title-bio-details-chard">
          {selectedProcessName
            ? `Resumen — ${selectedProcessName}`
            : "Resumen del proceso seleccionado"}
        </p>
        {!selectedProcessName && (
          <p className="table-default-column" style={{ marginBottom: "12px" }}>
            Haga clic en un proceso del gráfico para cargar las partes
            interesadas.
          </p>
        )}
        <SimpleTable
          data={detailRows}
          columns={summaryColumns}
          tableStyle={{ minWidth: "720px" }}
          isPaginated
          emptyMessage={
            selectedProcessName
              ? "No hay partes interesadas para este proceso."
              : "No se han encontrado registros"
          }
        />
      </div>
    </div>
  );
};

export default InterestedPartiesGraph;
